import { Injectable } from 'angular-ts-decorators';

import { Initable } from '@shared/app/scripts/services/initable';
import { SettingsManager } from '@shared/app/scripts/services/settings-manager';

import { ProductObject } from '@wholesale/app/scripts/classes/product-object';

export type ProductObjectFilter = {
  [key in keyof ProductObject]: ProductObject[key][];
};

import {
  each,
  escapeRegExp,
  filter,
  find,
  flow,
  groupBy,
  includes,
  intersection,
  isEmpty,
  keys,
  map,
  mapKeys,
  mapValues,
  merge,
  method,
  omit,
  orderBy,
  pick,
  reduce,
  size,
  some,
  sortBy,
  upperFirst,
} from 'lodash';

import { DataFeedAws } from '@shared/app/scripts/services/data-feed-aws';

/**
 * Service that provides information about products. Data format of product is:
 * <pre>
 * {
 *   // price data, all numbers, copied form first primary product
 *   price - the price wholesaler sells the piece ?
 *   suggestedRetailPrice
 *   localizedPrice - same as price, but in customer's currency
 *   localizedSuggestedRetailPrice - same as suggestedRetailPrice, but in customer's currency
 *   originalPrice - retail only field, it will always be 0 in wholesale
 *   buyPrice - price the wholesaler payed for the piece ?
 *   valuePrice - is the price that expresses the worth of an item for the owner.
 *                should never be shown the customer
 * </pre>
 */
@Injectable('products')
export class Products implements Initable {
  private productsDb: object;
  private inactiveBarcodes: any[];
  private activeBarcodes: any[];
  private allKeys: any[];

  private SKU_LEVEL_PROPERTIES: string[] = [
    'barcode',
    'colorCode',
    'colorCodeSupplier',
    'colorDescription',
    'colorFamily',
    'colorSupplier',
    'mainColorCode',
    'mainColorDescription',
    'size',
    'sizeIndex',
    'skuActive',
    'subColorCode',
    'subColorDescription',
    'warehouseLocation',
  ];
  private PRODUCT_LEVEL_PROPERTIES: string[] = [
    'active',
    'articleCode',
    'articleCodeSupplier',
    'articleDescription',
    'articleGroup',
    'brand',
    'buyPrice',
    'care',
    'collection',
    'directDelivery',
    'groups',
    'originalPrice',
    'price',
    'prices',
    'season',
    'sizes',
    'sizeRange',
    'suggestedRetailPrice',
    'supplierName',
    'tagType',
    'valuePrice',
    'vat',
    'year',
  ];
  private PRICE_LEVEL_PROPERTIES: string[] = ['price', 'suggestedRetailPrice'];

  public dependencies = ['dataFeedAws'];

  constructor(
    private $q: ng.IQService,
    private $log: ng.ILogService,
    private dataFeedAws: DataFeedAws,
    private settingsManager: SettingsManager,
  ) {
    'ngInject';

    this.productsDb = {};
    this.inactiveBarcodes = [];
    this.activeBarcodes = [];
    this.allKeys = [];
  }

  private localizePrices(priceOrProducts) {
    let value = pick(priceOrProducts, this.PRICE_LEVEL_PROPERTIES);

    value = mapKeys(value, (value, key) => 'localized' + upperFirst(key));

    value.localizedBasePrice = value.localizedPrice;

    return value;
  }

  private generateProductDb(dataFeed) {
    // Only store active products and active SKUs
    const filtered = filter(dataFeed, { active: true, skuActive: true });

    const grouped = groupBy(filtered, 'articleCode');

    return mapValues(grouped, (skuArray) => {
      const firstPrimarySku = find(skuArray, (obj: any) => obj.primary);

      const productObject: any = pick(
        firstPrimarySku,
        this.PRODUCT_LEVEL_PROPERTIES,
      );

      if (productObject.directDelivery) {
        if (
          this.settingsManager.settings.sales.showStock &&
          this.settingsManager.settings.sales.directDeliveryDefault
        ) {
          productObject.directDelivery = 'on';
        } else {
          productObject.directDelivery = 'off';
        }
      } else {
        productObject.directDelivery = 'unavailable';
      }

      productObject.skus = map(skuArray, (sku) => {
        return pick(sku, this.SKU_LEVEL_PROPERTIES);
      });

      return productObject;
    });
  }

  private generateInactiveSkus(dataFeed) {
    return dataFeed.reduce((accumulator, currentValue) => {
      if (
        angular.isDefined(currentValue.skuActive) &&
        currentValue.skuActive === false
      ) {
        accumulator.push({
          barcode: currentValue.barcode,
          articleCode: currentValue.articleCode,
        });
      }
      return accumulator;
    }, []);
  }

  private generateActiveSkus(dataFeed) {
    return dataFeed.reduce((accumulator, currentValue) => {
      if (
        angular.isDefined(currentValue.skuActive) &&
        currentValue.skuActive === true
      ) {
        accumulator.push({
          barcode: currentValue.barcode,
          articleCode: currentValue.articleCode,
        });
      }
      return accumulator;
    }, []);
  }

  private findArticleCodeByInactiveSku(barcode) {
    const inactiveProduct = find(this.inactiveBarcodes, { barcode: barcode });
    if (inactiveProduct) {
      return inactiveProduct.articleCode;
    }
  }

  /**
   * Query data from dataFeedAws and sets up internal database
   * @returns {promise} resolve when loaded
   */
  init() {
    this.dataFeedAws.skus$.subscribe((dataFeed) => {
      // Products should only be visible in the sales app if the ordershopcode exists
      dataFeed = filter(
        dataFeed,
        (obj) => !!find(obj.groups, { name: 'ordershopcode' }),
      );

      this.productsDb = this.generateProductDb(dataFeed);
      this.$log.info(
        `[Products] loaded ${size(this.productsDb)} active products`,
      );

      this.inactiveBarcodes = this.generateInactiveSkus(dataFeed);
      this.activeBarcodes = this.generateActiveSkus(dataFeed);

      this.$log.info(
        `[Products] found ${size(this.inactiveBarcodes)} inactive SKUs`,
      );

      this.allKeys = sortBy(keys(this.productsDb));
    });

    return this.$q.when();
  }

  /**
   * @param {boolean} active
   * active or inactive skus
   * gets size of local product database
   * @returns {number} the number of itmes
   */
  getSize(active) {
    return active ? size(this.productsDb) : size(this.inactiveBarcodes);
  }

  /**
   * @param {string} productCode
   * product code
   * gets product by productCode with default currency
   * @returns {ProductObject} product object, if found in internal db, null otherwise
   */
  get(productCode): ProductObject {
    if (angular.isDefined(this.productsDb[productCode])) {
      // making copy of the object & skip the prices - to avoid the abuse
      return new ProductObject(
        merge(
          omit(this.productsDb[productCode], ['prices']),
          this.localizePrices(this.productsDb[productCode]),
        ),
      );
    } else {
      return null;
    }
  }

  /**
   * @param {string} productCode
   * product code
   * @param {object} currency
   * currency object
   * gets product by productCode, sets the currency to the requested or use the
   * default if requested is not available
   * @returns {object|null} product object, found in internal db, null otherwise
   */
  getInCurrency(
    productCode: string,
    currency: { label: string },
  ): ProductObject {
    if (angular.isDefined(this.productsDb[productCode])) {
      const price = find(this.productsDb[productCode].prices, {
          currency: currency.label,
        }),
        product = omit(this.productsDb[productCode], ['prices']);

      if (price) {
        return new ProductObject(merge(product, this.localizePrices(price)));
      } else {
        // making copy of the object & skip the prices - to avoid the abuse
        return new ProductObject(
          merge(product, this.localizePrices(this.productsDb[productCode])),
        );
      }
    } else {
      return null;
    }
  }

  /**
   * gets all products by with default currency
   * @returns {ProductObject[]} array with all product objects, if internal db empty, empty array
   */
  getAll(): ProductObject[] {
    return map(this.allKeys, (key: any) => {
      return this.get(key);
    });
  }

  /**
   * filters all products by currency and agent filter setting
   * @param {string} currency
   * sales' currency id
   * @param { string } groupName
   * name of group to filter by
   * @param { string[] } groupValue
   * Array of expected values of the group
   * @returns {ProductObject[]} array with product objects, if internal db empty, empty array
   */
  getFilteredInCurrency(
    currency: string,
    groupName: string,
    groupValues: string[],
  ): ProductObject[] {
    const allProducts = this.getAllInCurrency(currency);

    // if no filter, return all
    if (isEmpty(groupName) || isEmpty(groupValues)) {
      return allProducts;
    }

    return filter(allProducts, (product: ProductObject) => {
      // drop if doesn't have the group
      const matchingGroups = filter(product.groups, { name: groupName });

      if (matchingGroups.length === 0) {
        return false;
      }

      return (
        intersection(
          map(matchingGroups, (group) => group.value),
          groupValues,
        ).length > 0
      );
    });
  }

  /**
   * @param {string} currency
   * sales' currency id
   * gets all products by with, for each sets the currency to the requested or use the
   * default if requested is not available
   * @returns {ProductObject[]} array with product objects, if internal db empty, empty array
   */
  getAllInCurrency(currency): ProductObject[] {
    return map(this.allKeys, (key) => {
      return this.getInCurrency(key, currency);
    });
  }

  /**
   * Returns tags for the given property and products as an object.
   * The returned data looks like
   * ```
   * {
   *  filterProperty: ['filterValue']
   * }
   * ```
   * @param {String} filterProperty a filter property
   * @param {Array} productsList array of products to create tags from
   * @returns {Object} filter
   */
  private generateFilterTag(filterProperty, productsList) {
    return {
      [filterProperty]: orderBy(
        reduce(
          productsList,
          (filterTags, product) => {
            switch (filterProperty) {
              case 'season':
              case 'collection': {
                if (filterProperty in product) {
                  if (!includes(filterTags, product[filterProperty])) {
                    filterTags.push(product[filterProperty]);
                  }
                }
                break;
              }
              default: {
                let groupProperty: any = find(product.groups, {
                  name: filterProperty,
                });

                if (groupProperty) {
                  if (!includes(filterTags, groupProperty.value)) {
                    filterTags.push(groupProperty.value);
                  }
                }
              }
            }
            return filterTags;
          },
          [],
        ),
      ),
    };
  }

  /**
   * Generates a filterTag object based on the given filters and available products
   * The returned data looks like
   * ```
   * {
   *   filterProperty1: ['filterValue'],
   *   filterProperty2: ['filterValue'],
   *   filterProperty3: ['filterValue']
   * }
   * ```
   * @param {[String]} filterProperties array of filter property names
   * @param {[Object]} products array of products
   * @returns {Object} filterTag object
   */
  generateFilterTags(filterProperties, products) {
    if (!angular.isArray(filterProperties) || !angular.isArray(products)) {
      return {};
    }

    if (!(filterProperties.length > 0)) {
      return {};
    }

    return Object.assign(
      // @ts-ignore
      ...filterProperties.map((filterProperty) =>
        this.generateFilterTag(filterProperty, products),
      ),
    );
  }

  /**
   * It uses the given filters over each product object in products and creates
   * a new filtered array of products.
   * @param {Object} filterTags
   * filter tag object with filter property as key and active tags as value
   * example:
   * ```
   * {
   *   filterProperty1: ['filterValue', 'value 2' ]
   * }
   * ```
   * @param {Array} products products it should filter
   * @returns {Array} array of filtered products or if none filtered it will return the given products array
   */
  filterByTags(filterTags, products) {
    let filteredProducts = products;

    if (!angular.isObject(filterTags) || !angular.isArray(products)) {
      return [];
    }

    each(Object.keys(filterTags), (group) => {
      let value = filterTags[group];

      if (value.length === 0) {
        return;
      }

      switch (group) {
        case 'season':
        case 'collection': {
          filteredProducts = filter(filteredProducts, (product) => {
            return includes(value, product[group]);
          });

          break;
        }
        default: {
          filteredProducts = filter(filteredProducts, (product) => {
            let productValues = map(
              filter(product.groups, { name: group }),
              'value',
            );

            return intersection(productValues, value).length > 0;
          });
        }
      }
    });

    return filteredProducts;
  }

  /**
   * Return all articles that have searchTerm in their articleCode,
   * articleDescription, or articleCodeSupplier
   * @param {String} searchTerm string that we search with
   * @param {Array} productsList products it should filter
   * @returns {Array} array of filtered products or if none filtered it will return the given products array
   */
  omniSearch(searchTerm, productsList) {
    if (!angular.isString(searchTerm) || !angular.isArray(productsList)) {
      return [];
    }

    if (searchTerm === '') {
      return productsList;
    }

    const normalizedSearchTerm = searchTerm.toLowerCase();

    function hasItAsArticleTerm(product) {
      return (
        angular.isString(product.articleCode) &&
        product.articleCode.toLowerCase().indexOf(normalizedSearchTerm) >= 0
      );
    }

    function hasItAsArticleDescription(product) {
      return (
        angular.isString(product.articleDescription) &&
        product.articleDescription
          .toLowerCase()
          .indexOf(normalizedSearchTerm) >= 0
      );
    }

    function hasItAsArticleCodeSupplier(product) {
      return (
        angular.isString(product.articleCodeSupplier) &&
        product.articleCodeSupplier
          .toLowerCase()
          .indexOf(normalizedSearchTerm) >= 0
      );
    }

    function hasItAsSkusBarcode(product) {
      return (
        angular.isArray(product.skus) &&
        some(product.skus, { barcode: searchTerm })
      );
    }

    // TODO find a better way to filter it
    return flow([
      (x) => filter(x, (product) => angular.isObject(product)),
      (x) =>
        filter(
          x,
          (product) =>
            hasItAsArticleTerm(product) ||
            hasItAsArticleDescription(product) ||
            hasItAsArticleCodeSupplier(product) ||
            hasItAsSkusBarcode(product),
        ),
    ])(productsList);
  }

  /**
   * Returns a single product or undefined that corresponds with the given filter
   * First tries to match on barcode then supplier and as last description
   * @param {String} searchTerm string that we want to search with
   * @param {Array} productsList products which we will filter on
   * @returns {object|undefined} a product or undefined if article is not found
   */
  singleSearch(searchTerm, productsList: any) {
    if (!angular.isString(searchTerm) || !angular.isArray(productsList)) {
      return undefined;
    }
    if (find(productsList, (x) => !angular.isObject(x))) {
      return undefined;
    }

    // @ts-ignore
    let product = find(productsList, { skus: [{ barcode: searchTerm }] });

    if (product) {
      return product;
    }

    // Search by articleCode which can be a partial case-insensitive match
    product = find(
      productsList,
      method('articleCode.match', new RegExp(escapeRegExp(searchTerm), 'i')),
    );
    if (product) {
      return product;
    }

    // Search by articleCodeSupplier which can be a partial case-insensitive match
    product = find(
      productsList,
      method(
        'articleCodeSupplier.match',
        new RegExp(escapeRegExp(searchTerm), 'i'),
      ),
    );
    if (product) {
      return product;
    }

    // Search by articleDescription which can be a partial case-insensitive match
    product = find(
      productsList,
      method(
        'articleDescription.match',
        new RegExp(escapeRegExp(searchTerm), 'i'),
      ),
    );

    if (product) {
      return product;
    }

    // Search through inactive products
    let articleCode = this.findArticleCodeByInactiveSku(searchTerm);
    product = find(productsList, { articleCode });

    if (product) {
      return product;
    }

    return undefined;
  }

  hasActiveBarcode(barcode) {
    const activeProduct = find(this.activeBarcodes, { barcode: barcode });

    return !!activeProduct;
  }
}
